/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { loadingIndicatorSelectors } from '@opengeoweb/store';
import { openLayersGetMapImageStore } from '@opengeoweb/webmap-react';
import LoadingIndicatorProgressBar from './LoadingIndicatorProgressBar';

const MILLISECONDS_PER_SECOND = 1e3;
export const MAX_DURATION_IN_SECONDS = 60;
export const MIN_DURATION_IN_SECONDS = 0.1;
export const DECIMALS_IN_LABEL = 1;
export const DECIMALS_IN_TOOLTIP = 3;

const LoadDuration: React.FC<{ mapId: string; layerId: string }> = ({
  mapId,
  layerId,
}) => {
  const [loadDurationLabel, percentage] = useLoadDurationLabel(mapId, layerId);
  const [tooltipText, setTooltipText] = React.useState<string | undefined>();

  return (
    <Tooltip
      onClose={() => setTooltipText(undefined)}
      onOpen={() => {
        const duration = getImageLoadDuration(layerId);
        if (duration) {
          setTooltipText(getDurationTooltip(duration));
        }
      }}
      open={tooltipText !== undefined}
      placement="top"
      title={tooltipText}
    >
      <Typography
        component="div"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: 12,
          width: '100%',
          textAlign: 'center',
        }}
        variant="subtitle2"
      >
        {percentage < 100 && (
          <LoadingIndicatorProgressBar
            height={4}
            progressPercentage={percentage}
            top={17}
            width={32}
          />
        )}
        <span data-testid="loadDuration">{loadDurationLabel}</span>
      </Typography>
    </Tooltip>
  );
};

const getDurationTooltip = (milliSeconds?: number): string => {
  if (milliSeconds === undefined) {
    return '-';
  }

  return `${roundToDecimals(milliSeconds / MILLISECONDS_PER_SECOND, DECIMALS_IN_TOOLTIP)} s`;
};

const getImageLoadDuration = (layerId: string): number | undefined => {
  return openLayersGetMapImageStore.loadDuration.getLoadDurationForId(layerId);
};

const roundToDecimals = (num: number, decimals: number): string => {
  return num.toLocaleString('en', {
    maximumFractionDigits: decimals,
    useGrouping: false,
  });
};

const getLoadDurationLabel = (layerId: string): string => {
  const loadDurationMs = getImageLoadDuration(layerId);

  if (loadDurationMs === undefined) {
    return '-';
  }

  const loadDurationInSeconds = loadDurationMs / MILLISECONDS_PER_SECOND;

  if (loadDurationInSeconds > MAX_DURATION_IN_SECONDS) {
    return `> ${MAX_DURATION_IN_SECONDS.toLocaleString('en')} s`;
  }

  if (loadDurationInSeconds < MIN_DURATION_IN_SECONDS) {
    return `< ${MIN_DURATION_IN_SECONDS.toLocaleString('en')} s`;
  }

  return `${roundToDecimals(loadDurationInSeconds, DECIMALS_IN_LABEL)} s`;
};
/**
 * Hook to subscribe to changes in the load duration for a map layer.
 * This will only trigger a render when the load duration rounded down to seconds changes for the given layer and map combination
 * @param mapId
 * @param layerId
 * @returns {number}
 */
export const useLoadDurationLabel = (
  mapId: string,
  layerId: string,
): [string | undefined, number] => {
  // Selector which triggers update of all labels in the layer manager when percentage of the map changes.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const update = useSelector((store: CoreAppStore) =>
    loadingIndicatorSelectors.getMapLoadingPercentage(store, mapId),
  );
  return [
    getLoadDurationLabel(layerId),
    openLayersGetMapImageStore.loadDuration.getTotalLoadingPercentageForIds([
      layerId,
    ]).percentage,
  ];
};

export default LoadDuration;
